import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "../components/AuthLayout";
import { Button } from "../../../components/ui";
import googleIcon from "../../../assets/svg/auth/google.svg";
import { EXTERNAL_LINKS, ROUTES } from "../../../routes/paths";

/**
 * Collects the details and hands both routes to the consent screen. Nothing
 * here talks to Clerk: under the DPDP Act the account cannot be created before
 * consent is given, and consent is the next screen - so this one only decides
 * which way the user is going and carries the form along.
 */
const SignUpPage = () => {
  const navigate = useNavigate();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");

  // Validation states
  const [nameError, setNameError] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);

  const validateName = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) {
      return "Full name is required";
    }
    if (trimmed.length < 3) {
      return "Full name must be at least 3 characters";
    }
    if (!/^[a-zA-Z][a-zA-Z\s.'-]*$/.test(trimmed)) {
      return "Full name can only contain letters, spaces, . ' and -";
    }
    return "";
  };

  const validateEmail = (value: string): string => {
    if (!value) {
      return "Email is required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFullName(value);
    if (nameTouched) {
      setNameError(validateName(value));
    }
  };

  // Both blur handlers leave an empty field alone: it was never typed in, so
  // it is unused rather than wrong, and the Google button below would otherwise
  // flag the form on the way past it. Submitting still asks for both.
  const handleNameBlur = () => {
    if (!fullName) {
      setNameError("");
      return;
    }

    setNameTouched(true);
    setNameError(validateName(fullName));
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (emailTouched) {
      setEmailError(validateEmail(value));
    }
  };

  const handleEmailBlur = () => {
    if (!email) {
      setEmailError("");
      return;
    }

    setEmailTouched(true);
    setEmailError(validateEmail(email));
  };

  const handleSubmit = () => {
    const nameErr = validateName(fullName);
    const emailErr = validateEmail(email);

    setNameError(nameErr);
    setNameTouched(true);
    setEmailError(emailErr);
    setEmailTouched(true);

    if (nameErr || emailErr) {
      return;
    }

    // The details ride along in router state; `signUp.create` runs on the
    // consent screen once the boxes are ticked.
    navigate(ROUTES.consent, {
      state: { method: "email", fullName: fullName.trim(), email },
    });
  };

  // The Google handshake is started from the consent screen too - going
  // straight to Google from here would have Clerk create the account before
  // the user has agreed to anything.
  const handleGoogle = () => {
    navigate(ROUTES.consent, { state: { method: "google" } });
  };

  const isFormValid = !validateName(fullName) && !validateEmail(email);

  const inputClasses = (hasError: boolean) =>
    `w-full px-4 py-3.5 bg-white border rounded-lg text-[15px] text-gray-900 placeholder-[#9AA4AE] focus:outline-none transition-all ${
      hasError
        ? "border-red-500"
        : "border-[#DCE0E4] focus:border-[#00C2CB]"
    }`;

  return (
    <AuthLayout>
      <h1 className="text-[24px] leading-tight font-semibold text-[#1F2937]">
        Sign up
      </h1>
      <p className="mt-1 text-[14px] text-[#62636C]">
        Please enter your details to create account
      </p>

      <form
        className="mt-8 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div>
          <input
            type="text"
            value={fullName}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            autoComplete="name"
            className={inputClasses(!!nameError && nameTouched)}
            placeholder="Full name"
          />
          {nameError && nameTouched && (
            <p className="mt-1.5 text-xs text-red-500">{nameError}</p>
          )}
        </div>

        <div>
          <input
            type="email"
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            autoComplete="email"
            className={inputClasses(!!emailError && emailTouched)}
            placeholder="yourname@email.com"
          />
          {emailError && emailTouched && (
            <p className="mt-1.5 text-xs text-red-500">{emailError}</p>
          )}
        </div>

        {/* Clerk's bot check moved to the consent screen along with
            `signUp.create` - it has to be in the DOM where the call is made. */}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          disabled={!isFormValid}
          className="mt-2"
        >
          Create account
        </Button>

        {/* <p className="text-center text-[13px] leading-relaxed text-[#62636C]">
          By creating an account, you agree to our{" "}
          <a
            href={EXTERNAL_LINKS.privacyPolicy}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#00C2CB] font-medium hover:underline"
          >
            Privacy Policy
          </a>
        </p> */}
      </form>

      <div className="my-7 flex items-center gap-4">
        <span className="h-px flex-1 bg-[#E3E7EA]" />
        <span className="text-sm font-medium text-[#9AA4AE]">OR</span>
        <span className="h-px flex-1 bg-[#E3E7EA]" />
      </div>

      <Button
        variant="secondary"
        size="lg"
        fullWidth
        onClick={handleGoogle}
        startIcon={<img src={googleIcon} alt="" className="w-5 h-5" />}
      >
        Continue with Google
      </Button>

      <p className="mt-12 text-center text-[14px] text-[#3A4652]">
        Already have an account?{" "}
        <button
          type="button"
          onClick={() => navigate(ROUTES.login)}
          className="text-[#00C2CB] font-medium hover:underline"
        >
          Log in
        </button>
      </p>
    </AuthLayout>
  );
};

export default SignUpPage;
